/**
 * The Floor Plan step's two-card start (`floor-plan-page-start`, R1–R5; *Floor Plan Page* frame 01):
 * *Start from this twin*, with what the live twin holds counted before it is pressed, and *Draw a new
 * plan*. The PDF import card is gone — *Add Reference* is a tool on the plan in both routes.
 *
 * A page composition of design-system parts, as the design's components table treats the score card
 * ("only here; build on the page"): `Card` on glass, a sticker disc, `Icon` facts, a `Tag` and
 * `Button`. The live card takes `Card`'s on-glass `selected` look — its frost wash and glow — for
 * frame 01's emphasis on the live twin, the reading `EquipmentLandedSummary` also uses; `Card` emits
 * no selection semantics for it.
 *
 * Presentational: `OnboardingPlanner` owns the reads, the adoption and the store.
 */
import React from 'react';
import styled from 'styled-components';
import { Button, Card, DockedCard, Icon, Scrim, Tag, type IconName } from '@repo/design-system';
import {
  brand,
  fontSans,
  ink,
  inkOn,
  radius,
  rgba,
  semantic,
  space,
  surface,
  typeRole,
  zIndex,
} from '@repo/design-tokens';
import type { RegisterReadState } from '@repo/front-end-state';
import type { PlanContents } from '@repo/front-end-utils';
import { visuallyHidden } from '@repo/ui';

/** The live card, as the live-model read and the adoption stand. */
export type TwinCardState =
  /** Not offered: a new twin, a plan already in progress, or no twin BID. */
  | { kind: 'hidden' }
  /** The route or the live read is not settled; `line` says which. */
  | { kind: 'loading'; line: string }
  /** It cannot be offered honestly: the reason is shown and the button is disabled. */
  | { kind: 'unavailable'; reason: string }
  | {
      kind: 'available';
      counts: PlanContents;
      /** How many entities the conversion skipped. */
      skipped: number;
      /** The production launch's completion (ISO 8601), on the manage route only. */
      liveSince: string | null;
      /** The adoption is saving: the button is busy and the status line says so. */
      adopting: boolean;
    };

/** `registerReadState`'s answer and the register's row count, for the draw card's fact. */
export interface DrawCardRegister {
  state: RegisterReadState;
  rows: number;
}

export interface StartChooserProps {
  /** A live or migrating twin (update wording) or a new one; `null` while the route is unknown. */
  existingTwin: boolean | null;
  twin: TwinCardState;
  register: DrawCardRegister;
  onStartFromTwin: () => void;
  onDrawNewPlan: () => void;
  /** A failed adoption's sentence, shown under the cards. */
  error: string | null;
  /**
   * `plain` (default): heading, cards and foot in the step's flow. `glass`: one docked card centred
   * over the design-system `Scrim` (`onboarding-persistent-scene-floor-plan-glass`, R7; canvas frame
   * 09): a cyan ribbon with the plan glyph, the heading and the sub line; the two cards; the foot.
   */
  look?: 'plain' | 'glass';
  /** The landing visit's draft notices, above the cards. */
  notices?: React.ReactNode;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** *3 floors · 43 spaces · 214 machines*, with cameras only when there are any (spec R4). */
export const contentsLine = ({ floors, spaces, machines, cameras }: PlanContents): string =>
  [
    plural(floors, 'floor', 'floors'),
    plural(spaces, 'space', 'spaces'),
    plural(machines, 'machine', 'machines'),
    ...(cameras > 0 ? [plural(cameras, 'camera', 'cameras')] : []),
  ].join(' · ');

export const skippedLine = (n: number): string =>
  n === 1
    ? '1 entity could not be read; it heads the Checks list'
    : `${n} entities could not be read; they head the Checks list`;

/** *This version live since 21 Apr 2026* — the production launch's date (spec Amendment 2). */
export const liveSinceLine = (iso: string): string | null => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const date = at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `This version live since ${date}`;
};

/** The draw card's register fact — never the empty sentence on a failed read (spec R5). */
export const registerLine = ({ state, rows }: DrawCardRegister): string => {
  if (state === 'error') return 'The register could not be read';
  if (state !== 'ready') return 'Reading the register…';
  if (rows === 0) return 'No assets in the register yet';
  return `${plural(rows, 'asset', 'assets')} in the register, none placed yet`;
};

interface Fact {
  icon: IconName;
  text: string;
}

const Facts: React.FC<{ facts: Fact[] }> = ({ facts }) => (
  <ul className="start-facts">
    {facts.map(f => (
      <li key={f.icon}>
        <Icon name={f.icon} variant="solid" size="sm" />
        <span>{f.text}</span>
      </li>
    ))}
  </ul>
);

const StartChooser: React.FC<StartChooserProps & { className?: string }> = ({
  className,
  existingTwin,
  twin,
  register,
  onStartFromTwin,
  onDrawNewPlan,
  error,
  look = 'plain',
  notices,
}) => {
  const since = twin.kind === 'available' && twin.liveSince ? liveSinceLine(twin.liveSince) : null;
  const twinFacts: Fact[] =
    twin.kind === 'available'
      ? [
          { icon: 'layer-group', text: contentsLine(twin.counts) },
          ...(twin.skipped > 0
            ? [{ icon: 'triangle-exclamation' as const, text: skippedLine(twin.skipped) }]
            : []),
          ...(since ? [{ icon: 'clock' as const, text: since }] : []),
        ]
      : [];
  /*
   * One status line, three sources, mutually exclusive — a single polite live region, so a
   * resolution that arrives after the card was announced is not silent.
   */
  const status =
    twin.kind === 'loading'
      ? twin.line
      : twin.kind === 'unavailable'
        ? twin.reason
        : twin.kind === 'available' && twin.adopting
          ? 'Loading the model into the planner…'
          : null;
  const twinReady = twin.kind === 'available' && !twin.adopting;
  const adopting = twin.kind === 'available' && twin.adopting;
  /*
   * The resolution, announced (criterion 12.4): the visible card shows *Ready* and the counts, but
   * neither is in the live region, so a screen reader heard *Checking…* and then nothing. Hidden,
   * because frame 01 has no visible status line in the ready state.
   */
  const readyAnnouncement = twinReady
    ? `Ready to start from this twin: ${contentsLine(twin.counts)}`
    : null;

  const heading =
    existingTwin === false
      ? 'How would you like to start your floor plan?'
      : 'How would you like to update this twin’s floor plan?';
  const cards = (
    <>
      <div className="start-cards">
        {twin.kind !== 'hidden' && (
          <Card
            surface="on-glass"
            size="lg"
            selected={twin.kind === 'available'}
            role="region"
            aria-labelledby="start-twin-title"
            className="start-card"
          >
            <div className="start-card-top">
              <span className="start-sticker is-live" aria-hidden="true">
                <Icon name="cube" variant="solid" size="xl" />
              </span>
              {twin.kind === 'available' && (
                <Tag surface="on-glass" size="dense" tone="info">
                  Ready
                </Tag>
              )}
            </div>
            <h3 id="start-twin-title" className="start-title">
              Start from this twin
            </h3>
            <p className="start-desc">
              Loads walls, spaces and equipment from the live twin, linked to the asset register.
              You start on Checks.
            </p>
            <Facts facts={twinFacts} />
            <p className="start-status" aria-live="polite">
              {status}
              {readyAnnouncement && <span style={visuallyHidden}>{readyAnnouncement}</span>}
            </p>
            <div className="start-action">
              <Button
                type="button"
                variant="primary"
                iconLeft={<Icon name="arrow-right" variant="solid" size="sm" />}
                // Saving is Button's busy state, not `disabled`: it blocks a second press and keeps
                // focus on the button, so a failed save leaves the keyboard where it was.
                disabled={twin.kind !== 'available'}
                loading={adopting}
                // Only while checking: an `aria-busy` passed here otherwise overrides `loading`'s.
                {...(twin.kind === 'loading' ? { 'aria-busy': true } : {})}
                onClick={() => {
                  // Belt to the `disabled` braces: a click in any other state does nothing.
                  if (twinReady) onStartFromTwin();
                }}
              >
                Start from This Twin
              </Button>
            </div>
          </Card>
        )}
        <Card
          surface="on-glass"
          size="lg"
          role="region"
          aria-labelledby="start-draw-title"
          className="start-card"
        >
          <div className="start-card-top">
            <span className="start-sticker" aria-hidden="true">
              <Icon name="pen-ruler" variant="solid" size="xl" />
            </span>
          </div>
          <h3 id="start-draw-title" className="start-title">
            Draw a new plan
          </h3>
          <p className="start-desc">
            A blank plan with one floor. Add a PDF or image to trace over, then place the register’s
            assets or drop products from the Library.
          </p>
          <Facts
            facts={[
              { icon: 'file-image', text: 'Reference plans: PDF or image, several per floor' },
              { icon: 'list', text: registerLine(register) },
              { icon: 'sitemap', text: 'Zones, sections and areas file themselves as you draw' },
            ]}
          />
          <div className="start-action">
            <Button
              type="button"
              variant="frosted"
              iconLeft={<Icon name="pen-ruler" variant="solid" size="sm" />}
              // Not while the twin is being loaded in: the two starts would race for the plan.
              disabled={adopting}
              onClick={onDrawNewPlan}
            >
              Draw a New Plan
            </Button>
          </div>
        </Card>
      </div>
      {error && (
        <p className="start-error" role="alert">
          {error}
        </p>
      )}
    </>
  );

  if (look === 'glass') {
    return (
      <div className={`${className ?? ''} is-glass`}>
        <Scrim />
        <div className="start-centre">
          <DockedCard
            tone="cyan"
            icon={<Icon name="map" variant="solid" />}
            title={heading}
            sub={SUB_LINE}
            footer={<p className="start-foot">{FOOT_LINE}</p>}
            className="start-glass-card"
          >
            {notices}
            {cards}
          </DockedCard>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <h2 className="start-heading">{heading}</h2>
      <p className="start-sub">{SUB_LINE}</p>
      {notices}
      {cards}
      <p className="start-foot">{FOOT_LINE}</p>
    </div>
  );
};

const SUB_LINE = 'Either way you get the same tools, and you can add reference plans at any time.';
/** Today's behaviour, said: the chooser shows only while no plan is in progress (R7.4). */
const FOOT_LINE = 'Back on this step later, the plan opens where you left it.';

/** Frame 01's geometry: two 380 px cards, a 56 px sticker. Component constants, not brand values. */
const CARD_WIDTH = '380px';
const STICKER_SIZE = '56px';

export default styled(StartChooser)`
  height: 100%;
  /*
   * The Floor Plan section clips (the planner owns its viewport), so the chooser scrolls itself:
   * at a narrow width or a high zoom the wrapped cards can be taller than the pane, and both starts
   * must stay reachable. Centred by auto margins on the heading (always first) and the footnote
   * (always last) rather than justify-content: center, which would push overflowing content off
   * the top where no scroll reaches it ("safe center" is not in Safari 15, which this app still
   * supports).
   */
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space[4]};
  padding: ${space[8]};
  font-family: ${fontSans};

  & > * {
    flex-shrink: 0;
  }

  .start-heading {
    margin: auto 0 0;
    font-family: ${typeRole.title2.fontFamily};
    font-size: ${typeRole.title2.fontSize};
    line-height: ${typeRole.title2.lineHeight};
    font-weight: ${typeRole.title2.fontWeight};
    color: ${ink[1]};
    text-align: center;
  }

  .start-sub,
  .start-foot {
    margin: 0;
    font-family: ${typeRole.body1.fontFamily};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    color: ${ink[2]};
    text-align: center;
  }

  .start-foot {
    margin-bottom: auto;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
  }

  .start-cards {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${space[6]};
    margin-top: ${space[4]};
  }

  .start-card {
    width: ${CARD_WIDTH};
    max-width: 100%;
  }

  .start-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  .start-sticker {
    width: ${STICKER_SIZE};
    height: ${STICKER_SIZE};
    border-radius: ${radius.pill};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${surface[0]};
    color: ${ink[2]};
    border: 2px solid ${surface[0]};
    box-shadow:
      0 0 0 1px ${rgba(brand.cyan, 0.08)},
      0 4px 14px ${brand.frostGlow};

    &.is-live {
      background: ${brand.frost};
      color: ${inkOn.aqua};
      box-shadow:
        0 0 0 1px ${rgba(brand.cyan, 0.1)},
        0 6px 18px ${rgba(brand.cyan, 0.3)};
    }
  }

  .start-title {
    margin: 0;
    font-family: ${typeRole.title3.fontFamily};
    font-size: ${typeRole.title3.fontSize};
    line-height: ${typeRole.title3.lineHeight};
    font-weight: ${typeRole.title3.fontWeight};
    color: ${ink[1]};
  }

  .start-desc {
    margin: 0;
    font-family: ${typeRole.body1.fontFamily};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    color: ${ink[2]};
  }

  .start-facts {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${space[2]};

    li {
      display: flex;
      align-items: center;
      gap: ${space[2]};
      font-family: ${typeRole.caption1.fontFamily};
      font-size: ${typeRole.caption1.fontSize};
      line-height: ${typeRole.caption1.lineHeight};
      color: ${ink[2]};
    }
  }

  .start-status {
    margin: 0;
    font-family: ${typeRole.caption1.fontFamily};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.muted};
  }

  .start-action {
    margin-top: ${space[2]};
  }

  /*
   * The glass look (R7, frame 09): the scrim over the step, and the card centred on it. The step list
   * and the nav bar sit outside the step, so they stay clear and reachable. The card keeps its
   * chrome; only its width follows the two cards it holds, and it scrolls inside itself when short
   * of height.
   */
  &.is-glass {
    position: relative;
    overflow: hidden;
    padding: 0;
    display: block;
  }

  /* Centred on the window, not the step (R7.1): the card clears the step list and the bar at every
     width the app supports, and passes pointers outside itself through to them. */
  .start-centre {
    position: fixed;
    inset: 0;
    /* Above its scrim, as a dialog is (the design system's layer order). */
    z-index: ${zIndex.modal};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${space[6]};
    pointer-events: none;
  }

  .start-glass-card {
    /* Its own height, not the slot's: the docked card stretches in a column by default. */
    align-self: center;
    height: auto;
    width: auto;
    max-width: 100%;
    max-height: 100%;
    pointer-events: auto;

    .start-cards {
      margin-top: 0;
      gap: ${space[4]};
    }

    .start-foot {
      margin: 0 auto;
    }
  }

  .start-error {
    max-width: 40rem;
    margin: 0;
    color: ${semantic.text.error};
    font-family: ${typeRole.body1.fontFamily};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    text-align: center;
  }
`;
